<template>
  <!-- Wraps Nuxt UI's FormField; adds the six-state indicator, scroll-to-first-error id, and TanStack FieldHandle wiring. -->
  <div :data-form-error-field="name">
    <FieldFrame
      v-bind="frameBindings"
      :orientation="orientation"
      :ui="layoutUi"
    >
      <template
        v-for="slotName in forwardedFieldSlots()"
        :key="slotName"
        #[slotName]
      >
        <slot :name="slotName" />
      </template>

      <template
        v-if="hasIndicator"
        #hint
      >
        <VisuallyHidden v-if="isRequiredIndicator">{{
          indicatorAccessibleName($t)
        }}</VisuallyHidden>

        <span
          v-else
          role="img"
          :class="[structure['indicator'], indicatorSkin]"
          :aria-label="indicatorAccessibleName($t)"
        />
      </template>

      <template #default="control">
        <slot
          :id="control.id"
          name="control"
          :describedby="control.describedby"
          :invalid="control.invalid"
          :disabled="disabled"
          :required="required"
        />
      </template>
    </FieldFrame>

    <LiveRegion
      v-if="!silent"
      :message="message"
      role="alert"
    />
  </div>
</template>

<script setup lang="ts" generic="TValue">
import { computed, toRefs } from 'vue'

import type { FieldHandle } from '~/interfaces/FieldHandle'
import { isFilledValue } from '~/utils/isFilledValue'
import { resolveFieldIndicator } from '~/utils/resolveFieldIndicator'
import type { FieldIndicator } from '~/utils/FieldIndicator'

import { fieldControlUi } from '../../base/fieldControlUi'
import VisuallyHidden from '../../atoms/VisuallyHidden/VisuallyHidden.atom.vue'
import LiveRegion from '../../atoms/LiveRegion/LiveRegion.atom.vue'
import { useFieldPresentation } from '~/design-system/molecules/useFieldPresentation'
import FieldFrame from '../FieldFrame/FieldFrame.molecule.vue'
import { useFieldFrame } from '../FieldFrame/useFieldFrame'

import structure from './FormField.structure.module.css'
import skin from './FormField.skin.module.css'

const props = withDefaults(
  defineProps<{
    /** Wire name. Emitted as data-form-error-field for scroll-to-first-error. */
    name: string
    /** Visible label text, already translated. */
    label: string
    /** Field handle from useTanstackForm's `fields` map; omit for a control the form library does not track. */
    field?: FieldHandle<TValue> | undefined
    /** Resolved client-side message, i.e. getFieldError(name). */
    error?: string
    /** Submit-only failure mapped via setFieldServerErrors. */
    serverError?: string
    /** Supplementary explanation revealed by an info icon after the label. */
    hint?: string
    /** Instructions shown under the control; they stay visible while an error shows. */
    help?: string
    /** Overrides the indicator's accessible name; empty uses the name for its current state. */
    indicatorLabel?: string
    /** Whether the field passed schema validation; bind useTanstackForm's `isFieldValidated(name)`. */
    validated?: boolean
    /** Horizontal places the label beside the control instead of above it. */
    orientation?: 'horizontal' | 'vertical'
    /** Marks the field as mandatory: the label carries the one visible asterisk; the indicator announces it. */
    required?: boolean
    /** Passed through to the control slot; does not style the label. */
    disabled?: boolean
    /** Suppresses the aria-live mirror, for forms that announce errors centrally. */
    silent?: boolean
  }>(),
  {
    disabled: false,
    error: '',
    field: undefined,
    help: '',
    hint: '',
    indicatorLabel: '',
    orientation: 'vertical',
    required: false,
    serverError: '',
    silent: false,
    validated: false
  }
)

defineSlots<{
  /** The input itself. Receives the wiring it must bind to be accessible. */
  control?: (props: {
    id: string
    describedby: string | undefined
    invalid: boolean
    disabled: boolean
    required: boolean
  }) => unknown
  /** Replaces the help copy, e.g. with a status badge. */
  help?: () => unknown
}>()

const { message } = useFieldPresentation({
  error: () => props.error,
  help: () => props.help,
  serverError: () => props.serverError
})
const { help, hint, label, name, required } = toRefs(props)
const { frameBindings, forwardedFieldSlots } = useFieldFrame(
  { help, hint, label, name, required },
  message,
  ['help']
)

const hasIndicator = computed(() => props.field !== undefined && props.label !== '')

const isHorizontal = computed(() => props.orientation === 'horizontal')

// Restores the label/control column split Nuxt UI's unqualified horizontal flex row does not give.
const layoutUi = computed(() =>
  isHorizontal.value
    ? {
        ...fieldControlUi.formField,
        container: 'mt-1 w-full lg:flex-1 lg:min-w-0 lg:mt-0 lg:w-auto',
        root: 'block lg:flex lg:gap-x-4 lg:place-items-baseline'
      }
    : fieldControlUi.formField
)

const indicatorState = computed<FieldIndicator>(() =>
  resolveFieldIndicator({
    error: props.error,
    filled: isFilledValue(props.field?.state.value),
    required: props.required,
    serverError: props.serverError,
    validated: props.validated
  })
)

const isRequiredIndicator = computed(() => indicatorState.value === 'required')

const indicatorSkins: Record<FieldIndicator, string | undefined> = {
  invalid: skin['indicator-invalid'],
  prefilled: skin['indicator-prefilled'],
  required: undefined,
  'server-error': skin['indicator-server-error'],
  unfilled: skin['indicator-unfilled'],
  valid: skin['indicator-valid']
}

const indicatorSkin = computed(() => indicatorSkins[indicatorState.value])

type Translate = (key: string) => string

const indicatorDefaultNames: Record<FieldIndicator, (t: Translate) => string> = {
  invalid: (t) => t('common.accessibility.fieldIndicator.invalid'),
  prefilled: (t) => t('common.accessibility.fieldIndicator.prefilled'),
  required: (t) => t('common.accessibility.fieldIndicator.required'),
  'server-error': (t) => t('common.accessibility.fieldIndicator.serverError'),
  unfilled: (t) => t('common.accessibility.fieldIndicator.unfilled'),
  valid: (t) => t('common.accessibility.fieldIndicator.valid')
}

const indicatorAccessibleName = (translate: Translate): string =>
  props.indicatorLabel === ''
    ? indicatorDefaultNames[indicatorState.value](translate)
    : props.indicatorLabel
</script>
